/* eslint @typescript-eslint/strict-boolean-expressions: off */

import Link from "next/link";
import React from "react";
import VisuallyHidden from "@reach/visually-hidden";
import capitalize from "lodash/capitalize";
import styles from "gui/chart/chart.module.css";
import { Center } from "gui/center/center";
import { ChartAxis } from "gui/chart/axis";
import { ChartLegend } from "gui/chart/legend";
import { ChartShapeArea } from "gui/chart/shapes";
import { ChartShapeColumn } from "gui/chart/shapes";
import { ChartShapeLine } from "gui/chart/shapes";
import { ChartShapePie } from "gui/chart/shapes";
import { ChartShapeScatter } from "gui/chart/shapes";
import { ChartTooltip } from "gui/chart/tooltip";
import { ErrorBoundary } from "gui/error/boundary";
import { Group } from "@visx/group";
import { ParentSize } from "@visx/responsive";
import { QueryResult } from "lib/results/types";
import { VisualChart } from "lib/visuals/types";
import { chartOptionOnlyPieCharts } from "gui/chart/options";
import { createChartBounds } from "gui/chart/bounds";
import { createChartScales } from "gui/chart/scales";
import {
  ChartSeries,
  createChartSeries,
  getGroupByYColumnName,
} from "gui/chart/series";
import { queryPath } from "lib/links/links";
import findKey from "lodash/findKey";
import keys from "lodash/keys";
import { useTheme } from "gui/theme/theme";

export const Chart: React.FC<{
  visual: VisualChart;
  result: QueryResult;
  watermark: boolean;
}> = (props) => {
  return (
    <figure className={styles.chart}>
      <ErrorBoundary>
        <ParentSize>
          {({ width, height }) => {
            return width > 0 && height > 0 ? (
              <ChartContentMemo {...props} width={width} height={height} />
            ) : null;
          }}
        </ParentSize>
      </ErrorBoundary>
    </figure>
  );
};

export interface ChartContentProps {
  visual: VisualChart;
  result: QueryResult;
  watermark: boolean;
  width: number;
  height: number;
}

export const ChartContent: React.FC<ChartContentProps> = (props) => {
  const [visual, setVisual] = React.useState(props.visual);
  const onlyPieCharts = chartOptionOnlyPieCharts(visual);
  const theme = useTheme();
  let series, bounds, scales, visualWithColors, groupColumnName;

  React.useEffect(() => {
    setVisual(props.visual);
  }, [props.visual]);

  try {
    series = createChartSeries(visual, props.result);
    bounds = createChartBounds(visual, series, props.width, props.height);
    // If theme.colors is set, override all the chart colors, by updating
    // seriesOptions or valueOptions on the visual
    // This allows for custom branding of external embeds.
    visualWithColors = theme.chart_colors
      ? applyCustomColors(visual, series, theme.chart_colors)
      : visual;
    scales = createChartScales(visualWithColors, series, bounds);

    groupColumnName = getGroupByYColumnName(
      props.visual.options.columnMapping,
      props.result.matrix?.columns
    );
  } catch (error) {
    if (error instanceof TypeError) {
      throw error;
    } else if (error instanceof Error) {
      return <Center>{error.message}</Center>;
    } else {
      return <Center>Something went wrong.</Center>;
    }
  }

  const config = {
    visual: visualWithColors,
    series,
    bounds,
    scales,
  };

  const seriesY0 = scales.y0 && (
    <>
      <ChartShapeColumn {...config} y={0} />
      <ChartShapeArea {...config} y={0} />
      <ChartShapeScatter {...config} y={0} />
      <ChartShapeLine {...config} y={0} />
      <ChartShapePie {...config} y={0} />
    </>
  );

  const seriesY1 = scales.y1 && (
    <>
      <ChartShapeColumn {...config} y={1} />
      <ChartShapeArea {...config} y={1} />
      <ChartShapeScatter {...config} y={1} />
      <ChartShapeLine {...config} y={1} />
      <ChartShapePie {...config} y={1} />
    </>
  );

  return (
    <>
      <ChartWatermark watermark={props.watermark} />
      <ChartCaption visual={visual} />
      <ChartLegend {...config} setVisual={setVisual} />
      <svg width={bounds.width} height={bounds.height}>
        <Group top={bounds.margin.top} left={bounds.margin.left}>
          {!onlyPieCharts && <ChartAxis {...config} />}
          {seriesY0}
          {seriesY1}
          {!onlyPieCharts && (
            <ChartTooltip groupColumnName={groupColumnName} {...config} />
          )}
        </Group>
      </svg>
    </>
  );
};

const ChartContentMemo = React.memo(ChartContent);

const ChartWatermark: React.FC<{
  watermark: boolean;
}> = (props) => {
  if (!props.watermark) {
    return null;
  }

  return (
    <img
      alt="Dune logo watermark"
      src="/assets/logo-512w.png"
      className={styles.watermark}
    />
  );
};

const ChartCaption: React.FC<{
  visual: VisualChart;
}> = (props) => {
  const queryId = props.visual.query_details?.query_id;

  const queryLink = queryId && (
    <Link href={queryPath(queryId) + "#results"}>
      <a>See query results table</a>
    </Link>
  );

  return (
    <VisuallyHidden>
      <figcaption>
        <p>{capitalize(props.visual.options.globalSeriesType)} chart</p>
        {queryLink && <p>{queryLink}</p>}
      </figcaption>
    </VisuallyHidden>
  );
};

// Explicitly override all chart colors with a list of custom colors.
// This will override any colors that were originally set by the author.
const applyCustomColors = (
  visual: VisualChart,
  series: ChartSeries,
  colors: string[]
): VisualChart => {
  if (visual.options.globalSeriesType === "pie") {
    return applyCustomValuesColors(visual, series, colors);
    // return visual;
  } else {
    return applyCustomSeriesColors(visual, series, colors);
  }
};

// Override chart *series* colors with custom brand colors.
// the *series.rows* contains the normalised data for the chart. This
// means that each item contains the x axis value and all the series values.
// This function uses that to extract the names of the series and updates the
// seriesOptions with the color for each series
const applyCustomSeriesColors = (
  visual: VisualChart,
  series: ChartSeries,
  colors: string[]
): VisualChart => {
  let index = 0;
  const seriesOptions = { ...visual.options.seriesOptions };
  // sort the columns to make sure we always get a deterministic order
  const sortedSeriesNames = keys(series.rows[0] || {}).sort();
  sortedSeriesNames.forEach((key) => {
    const value = visual.options.columnMapping
      ? visual.options.columnMapping[key]
      : undefined;
    // if the value is "y" it means that this column
    // will be a series value (eg values on line)
    if (value !== "x") {
      // If the column is on the seriesOption then override the color
      // property. Otherwise create an empty object with just the color
      // property
      seriesOptions[key] = {
        ...seriesOptions[key],
        color: colors[index],
      };
      // Increment the index so that we iterated through the colors
      index = (index + 1) % colors.length;
    }
  });
  return {
    ...visual,
    options: { ...visual.options, seriesOptions },
  };
};

// Override chart *values* colors with custom brand colors.
// the *series.rows* contains the normalised data for the chart. If you
// want to set the color for each value on the chart (eg. for pie charts)
// this function will extract the name of all the possible values by finding
// the name of the values and updating the relevant field in valueOptions
const applyCustomValuesColors = (
  visual: VisualChart,
  series: ChartSeries,
  colors: string[]
): VisualChart => {
  const valuesOptions = { ...visual.options.valuesOptions };
  let index = 0;
  // Find the name of the value for the x axis.
  const x = findKey(visual.options.columnMapping, (k) => k === "x")!;
  // For all the rows extract the value on the x axis and sort it so that we always
  // have a deterministic outcome
  const sortedValueNames: string[] = series.rows
    .map((row) => String(row[x]))
    .sort();

  // for each value on the x-axis set the color for
  // all the values at that point by updating *valueOptions[key]*
  sortedValueNames.forEach((name) => {
    valuesOptions[name] = {
      ...valuesOptions[name],
      color: colors[index],
    };
    // Increment the index so that we iterated through the colors
    index = (index + 1) % colors.length;
  });

  return {
    ...visual,
    options: { ...visual.options, valuesOptions },
  };
};
